import type { ComponentType, SVGProps } from "react";
import { Link, NavLink } from "react-router-dom";
import { BasilHome, BasilMessageCircle, BasilShield, BasilShoppingBag, BasilTrophy, BasilUser } from "@/components/icons/basil";
import { DrawerProfile, ProfileAvatar } from "@/components/layout/ProfileCard";
import { profileDisplay, useProfile } from "@/components/layout/useProfile";
import { cn } from "@shared/lib/utils";

type NavItem = {
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  to: string;
};

// 슬림 내비 — Packs·Collection·Missions·Wallet은 /profile 허브로 이동(라우트는 유지)
const NAV_ITEMS: NavItem[] = [
  { label: "Home", icon: BasilHome, to: "/home" },
  { label: "Shop", icon: BasilShoppingBag, to: "/shop" },
  { label: "Season", icon: BasilTrophy, to: "/season" },
  { label: "Agents", icon: BasilMessageCircle, to: "/agents" },
  { label: "Profile", icon: BasilUser, to: "/profile" },
];

/** Only rendered for users.is_admin. */
const ADMIN_NAV_ITEM: NavItem = { label: "Admin", icon: BasilShield, to: "/admin" };

/** Courtyard 내비 가독성 — Inter 14/600 sentence case, 아이콘은 currentColor 상속. */
const ITEM_BASE =
  "flex w-full items-center gap-3 rounded-lg px-4 py-2.5 text-left text-sm font-semibold outline-none transition-colors duration-200 focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-accent/60";

/** §2.1 active row — 2px gold bar on the left edge + 10% gold fill + gold text. */
const ITEM_ACTIVE =
  "relative rounded-l-none bg-[rgba(217,185,97,0.10)] text-accent before:absolute before:inset-y-0 before:left-0 before:w-0.5 before:bg-accent";

type SideNavProps = {
  /** Called after a route link is clicked (drawer close on mobile). */
  onNavigate?: () => void;
  /** Set false while the drawer is hidden so items leave the tab order. */
  tabbable?: boolean;
  /** "drawer" (default): mobile drawer layout — profile on top.
   *  "sidebar": lg+ fixed rail — CTA + hairline + profile pinned at the bottom. */
  variant?: "drawer" | "sidebar";
};

/** Nav list + community footer — mobile drawer content AND (variant="sidebar") the lg+ fixed rail. */
export function SideNav({ onNavigate, tabbable = true, variant = "drawer" }: SideNavProps) {
  const tabIndex = tabbable ? 0 : -1;
  const { user, authenticated, summary } = useProfile();
  const navItems = summary?.isAdmin ? [...NAV_ITEMS, ADMIN_NAV_ITEM] : NAV_ITEMS;
  const sidebar = variant === "sidebar";
  const display = profileDisplay(user);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Drawer: profile block on top — signed in: avatar + IP · RP + actions;
          signed out: Telegram CTA. `tabbable` doubles as the drawer-open signal
          that gates the lazy summary fetch. */}
      {!sidebar && (
        <DrawerProfile active={tabbable} tabbable={tabbable} onNavigate={onNavigate} />
      )}

      <nav aria-label="Primary" className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
        <ul className="flex flex-col gap-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.label}>
                <NavLink
                  to={item.to}
                  end={item.to === "/home"}
                  tabIndex={tabIndex}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn(
                      ITEM_BASE,
                      isActive ? ITEM_ACTIVE : "text-ink-dim hover:bg-white/5 hover:text-ink",
                    )
                  }
                >
                  <Icon className="size-[18px] shrink-0" aria-hidden />
                  {item.label}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Sidebar bottom block — ① CTA ② hairline ③ 미니 아이덴티티(이름 → /profile).
          잔액·무료팩·미션·계정 액션은 상단 HUD와 ProfileChip이 단일 소유 — 사이드바 중복 금지. */}
      {sidebar && (
        <>
          <div className="shrink-0 px-3 pb-4">
            <Link
              to="/shop"
              tabIndex={tabIndex}
              onClick={onNavigate}
              className="flex w-full items-center justify-center gap-1.5 rounded-(--ty-radius-btn) bg-(--ty-accent-fill) px-4 py-2.5 font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-accent outline-none transition-colors duration-200 hover:bg-[rgba(217,185,97,0.18)] focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-accent/60"
            >
              + Open packs
            </Link>
          </div>
          {authenticated && (
            <div className="shrink-0 border-t border-line">
              <Link
                to="/profile"
                tabIndex={tabIndex}
                onClick={onNavigate}
                aria-label={`Profile — ${display.name}`}
                className="flex items-center gap-3 px-5 py-4 outline-none transition-colors duration-200 hover:bg-white/4 focus-visible:outline-1 focus-visible:-outline-offset-2 focus-visible:outline-accent/60"
              >
                <ProfileAvatar display={display} className="size-8 text-xs" />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-ink">{display.name}</span>
                  {display.handle && (
                    <span className="block truncate font-mono text-[10px] text-ink-mute">
                      {display.handle}
                    </span>
                  )}
                </span>
              </Link>
            </div>
          )}
        </>
      )}

    </div>
  );
}
